import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Client, Population, PopulationService } from "../../../../swaggerServices";
import {
  PopulationRouteResolver,
  RoutePopulation
} from "../../clients/population-taxpayers/population-route-resolver.service";
import { Subscription, Subject } from "rxjs";
import { ClientRouteResolver } from "../../clients/client/client-route-resolver.service";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { UserInfoService } from "../../user-info.service";
import { ClientNotificationService } from "../../client-notification.service";
import { ProcessStatePollingService } from "../../../shared/services/process-state-polling.service";
import { Processes, ProcessStates } from "../../../shared/types/processState";
import { takeUntil } from "rxjs/operators";
import moment from 'moment';
import { TenantConfigService } from "../../../shared/services/tenant-config.service";
import { ConfigStateService } from "../../config/config-state.service";
import { TranslocoService} from "@jsverse/transloco";

declare const $: any;

@Component({
    selector: 'app-population-sidebar',
    templateUrl: './population-sidebar.component.html',
    styleUrls: ['./population-sidebar.component.scss'],
    standalone: false
})
export class PopulationSidebarComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  client?: Client;
  population;
  @ViewChild('editPopulationModal') editPopulationModalEl: ElementRef;
  @ViewChild('confirmDeletePopulation') confirmDeletePopulation: ElementRef;
  editedPopulationName?: string;
  taxYr: number = this.activatedRoute.queryParams['value'].taxYear;
  showDownloadPackageButton = false;
  generationMessage = '';
  packageDate = '';
  taxReturnAlias: string;
  yearLabel: string;
  uiConfig: TenantConfigService;


  private unsubscribe: Subject<void> = new Subject();
  constructor(private zone: NgZone,
    private populationService: PopulationService,
    private clientRouteResolver: ClientRouteResolver,
    private populationRouteResolver: PopulationRouteResolver,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public config: ConfigStateService,
    private clientNotificationService: ClientNotificationService,
    private processStatePollingService: ProcessStatePollingService,
    public userInfo: UserInfoService,
    private translocoService: TranslocoService) {
  }

  ngOnInit() {
    this.subscriptions.push(this.clientRouteResolver.client().subscribe(client => {
      this.client = client;
    }));
    this.subscriptions.push(this.populationRouteResolver.routePopulation().subscribe((routePopulation: RoutePopulation) => {
      this.population = routePopulation.population;

      this.showDownloadPackageButton = this.population.hasPackage;
      if(this.population.hasPackage){
        // we are only checking this for older packages that were run under the old method for bulk running package generation
        // it is expected that future bulk generation will use the CN package generator from the "All tax returns screen"
        this.checkPackageGenerationState();
      }
    }));
    this.checkPackageGenerationState();
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService.selectTranslate('year').subscribe(value => this.yearLabel = value);
        this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.userInfo.isClientAdmin) {
      if (this.editPopulationModalEl) {
        $(this.editPopulationModalEl.nativeElement).modal('hide');
      }
      if (this.confirmDeletePopulation) {
        $(this.confirmDeletePopulation.nativeElement).modal('hide');
      }
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }


  private checkPackageGenerationState() {

    this.processStatePollingService
      .subscribeData(Processes.EssGeneration, 'population', this.population.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (!response || !response.state || !response.message) {
          this.generationMessage = '';
          this.packageDate = '';
          return;
        }
        const isGenerating = response.state === ProcessStates.RUNNING;
        if (isGenerating) {
          this.packageDate = '';
          this.showDownloadPackageButton = false;
          this.generationMessage = response.message.replace('\n', '<br>');
        } else if (response.updatedAt) {
          this.showDownloadPackageButton = true;
          this.generationMessage = '';
          this.packageDate = `Generated at: <strong>${moment(response.updatedAt).format('DD MMM YYYY HH:mm')}</strong>`;
        } else {
          this.packageDate = '';
          this.generationMessage = '';
        }
      });
  }


  showPopulationEditModal() {
    if (this.canEditName()) {
      this.editedPopulationName = this.population.name;
      $(this.editPopulationModalEl.nativeElement).modal('show');
    }
  }

  // updates population name
  updatePopulationName() {
    if (!this.editedPopulationName) return false;
    const updatedPopulation = { ...this.population, name: this.editedPopulationName };
    this.populationService.updatePopulation(updatedPopulation).subscribe(() => {
      // just update the name only, as the return from "populationService.updatePopulation" is not the full population data structure
      this.populationRouteResolver.updatePopulation({ ...this.population, name: updatedPopulation.name });
      $(this.editPopulationModalEl.nativeElement).modal('hide');
    });
  }

  canEditName() {
    return this.userInfo.isClientAdmin && this.population && this.population.name.toLocaleLowerCase() !== 'unassigned';
  }

  canDelete() {
    return this.population.name.toLocaleLowerCase() !== 'unassigned'
      && this.population.assignees.every(a => a.status === 99);
  }

  confirmDelete() {
    $(this.confirmDeletePopulation.nativeElement).modal({
      onApprove: approve => approve
        ? this.populationService.deletePopulationById(this.population.id).subscribe(() => {
          if(this.taxYr){
            this.router.navigate(["clients", this.client.id, "populations"], { queryParams: { taxYear: Number(this.taxYr) } });
          } else {
            this.router.navigate(["clients", this.client.id, "populations"]);
          }
        })
        : false
    }).modal('show');
  }

  downloadPackage() {
    this.populationService.downloadPackage(this.population.id).subscribe(data => {
      if (!data.fileContent) {
        this.clientNotificationService.appendMessage("File couldn't be fetched, please try again later", "Download package", false);
        return;
      }
      var elem = window.document.createElement('a');
      elem.href = "data:application/zip;base64," + data.fileContent;
      elem.download = data.fileName ? data.fileName.replace('.zip', '') : "package";
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }, error => {
      this.clientNotificationService.appendMessage("File couldn't be fetched, please try again later", "Download package", false);
      return;
    });
  }
}
